
import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { adminPrivilegedService, AdminFamilyAnalytics } from '@/services/admin/adminPrivilegedService';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Users, Home, TrendingUp, Activity, Zap, BarChart3 } from 'lucide-react';

const AdminFamilyAnalyticsDisplay: React.FC = () => {
  const [analytics, setAnalytics] = useState<AdminFamilyAnalytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchAnalytics = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await adminPrivilegedService.getFamilyAnalytics();
        setAnalytics(data);
      } catch (err) {
        console.error('Failed to load family analytics:', err);
        setError(err instanceof Error ? err.message : 'Failed to load family analytics');
      } finally {
        setLoading(false);
      }
    };

    fetchAnalytics();
  }, []);

  if (loading) {
    return (
      <div>
        <h2 className="text-2xl font-bold mb-6 mt-2">Family Analytics</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {Array.from({ length: 6 }).map((_, i) => (
            <Card key={i}>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-4 w-4" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-8 w-16 mb-2" />
                <Skeleton className="h-4 w-20" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <h2 className="text-2xl font-bold mb-6 mt-2">Family Analytics</h2>
        <Card className="border-red-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-red-600 font-medium">Error loading family analytics</p>
                <p className="text-sm text-red-500 mt-1">{error}</p>
              </div>
              <Badge variant="destructive">Error</Badge>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!analytics) return null;

  const familySizeData = [
    { 
      title: '2-Member Families', 
      value: analytics.familySizeDistribution['2_members'] || 0,
      icon: Users,
      color: 'text-blue-600'
    },
    { 
      title: '3-Member Families', 
      value: analytics.familySizeDistribution['3_members'] || 0,
      icon: Users,
      color: 'text-green-600'
    },
    { 
      title: '4-Member Families', 
      value: analytics.familySizeDistribution['4_members'] || 0,
      icon: Users,
      color: 'text-orange-600'
    },
    { 
      title: '5+ Member Families', 
      value: analytics.familySizeDistribution['5_plus_members'] || 0,
      icon: Users,
      color: 'text-purple-600'
    }
  ];

  const engagementData = [
    {
      title: 'Active Families',
      value: analytics.familyEngagementBreakdown.active || 0,
      icon: Activity,
      color: 'text-green-600',
      description: 'Recent activity (30 days)'
    },
    {
      title: 'Dormant Families',
      value: analytics.familyEngagementBreakdown.dormant || 0,
      icon: Home,
      color: 'text-yellow-600',
      description: 'Past activity, no recent use'
    },
    {
      title: 'Inactive Families',
      value: analytics.familyEngagementBreakdown.inactive || 0,
      icon: Home,
      color: 'text-gray-600',
      description: 'No recorded activity'
    }
  ];

  const activityDistributionData = [
    {
      title: 'Low Activity Families',
      value: analytics.activeFamilyActivityDistribution.low,
      icon: BarChart3,
      color: 'text-yellow-600',
      description: '1-10 activities/month'
    },
    {
      title: 'Medium Activity Families',
      value: analytics.activeFamilyActivityDistribution.medium,
      icon: TrendingUp,
      color: 'text-blue-600',
      description: '11-50 activities/month'
    },
    {
      title: 'High Activity Families',
      value: analytics.activeFamilyActivityDistribution.high,
      icon: Zap,
      color: 'text-red-600',
      description: '50+ activities/month'
    }
  ];

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold mt-2">Family Analytics</h2>
        <Badge variant="default" className="bg-green-100 text-green-800">
          Live Data
        </Badge>
      </div>
      
      <div className="space-y-8">
        {/* Family Size Distribution */}
        <div>
          <h3 className="text-lg font-semibold mb-4">Family Size Distribution</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {familySizeData.map((item, index) => (
              <Card key={index}>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">{item.title}</CardTitle>
                  <item.icon className={`h-4 w-4 ${item.color}`} />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{item.value}</div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Family Engagement Breakdown */}
        <div>
          <h3 className="text-lg font-semibold mb-4">Family Engagement Status</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {engagementData.map((item, index) => (
              <Card key={index}>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">{item.title}</CardTitle>
                  <item.icon className={`h-4 w-4 ${item.color}`} />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold mb-1">{item.value}</div>
                  <p className="text-xs text-muted-foreground">{item.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Activity Distribution */}
        <div>
          <h3 className="text-lg font-semibold mb-4">Active Family Activity Levels</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {activityDistributionData.map((item, index) => (
              <Card key={index}>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">{item.title}</CardTitle>
                  <item.icon className={`h-4 w-4 ${item.color}`} />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold mb-1">{item.value}</div>
                  <p className="text-xs text-muted-foreground">{item.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Key Insights */}
        <div>
          <h3 className="text-lg font-semibold mb-4">Key Insights</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Avg Activities/Active Family</CardTitle>
                <BarChart3 className="h-4 w-4 text-blue-600" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{analytics.averageActivitiesPerActiveFamily}</div>
                <p className="text-xs text-muted-foreground">Per active family (30-day period)</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Top 10 Families Activity Share</CardTitle>
                <Zap className="h-4 w-4 text-red-600" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{analytics.powerFamilyStats.top10FamiliesActivityShare}%</div>
                <p className="text-xs text-muted-foreground">
                  {analytics.powerFamilyStats.top10FamiliesCount} families drive this activity
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminFamilyAnalyticsDisplay;
